import { useEffect, useState } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Typography
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import ButtonStyled from '../components/ButtonStyled';
import { getUser, userRequestPasswordChange } from '../services/userService';

const formLabelStyle = {
    color: '#303030',
    fontWeight: 'bold',
    fontSize: { xs: '0.875rem', md: '1rem' },
    pb: '0.2rem'
};

const inputStyle = {
    sx: {
        fontSize: { xs: '1rem', md: '1rem' },
        background: '#FFFEFC',
        borderRadius: '0.25rem'
    }
};

const formControlStyle = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '0.25rem',
        backgroundColor: 'white',
        '&.Mui-focused fieldset': {
            borderColor: '#FC581F'
        }
    },
    '& .MuiSvgIcon-root': {
        color: '#7C7B7B'
    }
};

const defaultErrors = {
    currentPasswordError: false,
    newPasswordError: false,
    confirmNewPasswordError: false
};

const ChangePasswordPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [errors, setErrors] = useState(defaultErrors);
    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const userId = localStorage.getItem('UID') || '';

    useEffect(() => {
        if (!userId) {
            navigate('/');
            return;
        }

        getUser(userId)
            .then((response) => {
                setUserEmail(response?.user?.email || '');
            })
            .catch(() => {
                enqueueSnackbar('No fue posible cargar la información del usuario.', {
                    variant: 'error'
                });
            });
    }, [navigate, userId, enqueueSnackbar]);

    const updateForm = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value
        }));

        if (errors[`${name}Error`]) {
            setErrors((prev) => ({
                ...prev,
                [`${name}Error`]: false
            }));
        }
    };

    const validatePassword = (str) => {
        if (str.length < 8) return false;
        if (!/[A-Z]/.test(str)) return false;
        if (!/\d/.test(str)) return false;
        return true;
    };

    const validateForm = () => {
        const newErrors = {
            currentPasswordError: !form.currentPassword,
            newPasswordError: !form.newPassword,
            confirmNewPasswordError: !form.confirmNewPassword
        };

        setErrors((prev) => ({ ...prev, ...newErrors }));

        if (Object.values(newErrors).some(Boolean)) {
            enqueueSnackbar('Todos los campos son obligatorios.', {
                variant: 'error'
            });
            return false;
        }

        if (!validatePassword(form.newPassword)) {
            setErrors((prev) => ({ ...prev, newPasswordError: true }));
            enqueueSnackbar('La nueva contraseña debe tener al menos 8 caracteres, una letra mayúscula y un número.', {
                variant: 'error'
            });
            return false;
        }

        if (form.currentPassword === form.newPassword) {
            setErrors((prev) => ({ ...prev, newPasswordError: true }));
            enqueueSnackbar('La nueva contraseña debe ser diferente a la contraseña actual.', {
                variant: 'error'
            });
            return false;
        }

        if (form.newPassword !== form.confirmNewPassword) {
            setErrors((prev) => ({ ...prev, confirmNewPasswordError: true }));
            enqueueSnackbar('La nueva contraseña y su confirmación no coinciden.', {
                variant: 'error'
            });
            return false;
        }

        return true;
    };

    const onSubmit = async () => {
        if (isLoading) return;

        if (!userId || !userEmail) {
            navigate('/');
            return;
        }

        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const result = await userRequestPasswordChange({
                email: userEmail,
                currentPassword: form.currentPassword,
                newPassword: form.newPassword
            });

            if (result?.result) {
                enqueueSnackbar(result.message || 'Contraseña actualizada con éxito.', {
                    variant: 'success'
                });
                navigate('/admin/account');
            } else {
                enqueueSnackbar(result?.message || 'No fue posible actualizar la contraseña.', {
                    variant: 'error'
                });
            }
        } catch {
            enqueueSnackbar('No fue posible actualizar la contraseña.', {
                variant: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ px: { xs: '1.5rem', md: '3rem' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', py: { xs: '1.5rem', md: '2rem' } }}>
                <IconButton
                    sx={{ padding: 0 }}
                    onClick={() => {
                        navigate('/admin/account');
                    }}
                >
                    <NavigateNextIcon
                        sx={{
                            transform: 'rotate(180deg)',
                            fontSize: { xs: '1.5rem', md: '2rem' },
                            color: '#FC581F'
                        }}
                    />
                </IconButton>
                <Typography sx={{ fontSize: { xs: '1.125rem', md: '1.5rem' }, fontWeight: 'bold' }}>
                    CAMBIAR CONTRASEÑA
                </Typography>
            </Box>

            <Box
                sx={{
                    border: '1px solid #E6E6E6',
                    borderRadius: '0.5rem',
                    p: { xs: '1rem', md: '1.5rem' },
                    boxShadow: '1px 1px 4px 0px rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    maxWidth: '42rem'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <LockResetOutlinedIcon sx={{ color: '#FC581F' }} />
                    <Typography sx={{ fontWeight: 'bold', color: '#303030', fontSize: { xs: '1rem', md: '1.125rem' } }}>
                        Seguridad
                    </Typography>
                </Box>

                <Typography sx={{ color: '#575757', fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Ingresa tu contraseña actual y define una nueva contraseña.
                </Typography>

                <FormControl variant='standard' fullWidth sx={formControlStyle}>
                    <FormLabel focused={false} sx={formLabelStyle}>
                        CONTRASEÑA ACTUAL
                    </FormLabel>
                    <OutlinedInput
                        inputProps={inputStyle}
                        fullWidth
                        size='small'
                        type={showCurrentPassword ? 'text' : 'password'}
                        name='currentPassword'
                        value={form.currentPassword}
                        placeholder='********'
                        onChange={updateForm}
                        error={errors.currentPasswordError}
                        endAdornment={
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={() => setShowCurrentPassword((prev) => !prev)}
                                    edge='end'
                                >
                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>

                <FormControl variant='standard' fullWidth sx={formControlStyle}>
                    <FormLabel focused={false} sx={formLabelStyle}>
                        NUEVA CONTRASEÑA
                    </FormLabel>
                    <OutlinedInput
                        inputProps={inputStyle}
                        fullWidth
                        size='small'
                        type={showNewPassword ? 'text' : 'password'}
                        name='newPassword'
                        value={form.newPassword}
                        placeholder='********'
                        onChange={updateForm}
                        error={errors.newPasswordError}
                        endAdornment={
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={() => setShowNewPassword((prev) => !prev)}
                                    edge='end'
                                >
                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>

                <FormControl variant='standard' fullWidth sx={formControlStyle}>
                    <FormLabel focused={false} sx={formLabelStyle}>
                        CONFIRMAR NUEVA CONTRASEÑA
                    </FormLabel>
                    <OutlinedInput
                        inputProps={inputStyle}
                        fullWidth
                        size='small'
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        name='confirmNewPassword'
                        value={form.confirmNewPassword}
                        placeholder='********'
                        onChange={updateForm}
                        error={errors.confirmNewPasswordError}
                        endAdornment={
                            <InputAdornment position='end'>
                                <IconButton
                                    onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                                    edge='end'
                                >
                                    {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </FormControl>

                <Typography sx={{ color: '#7C7B7B', fontSize: '0.875rem' }}>
                    La contraseña debe tener al menos 8 caracteres, una letra mayúscula y un número.
                </Typography>

                <Box sx={{ pt: '0.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonStyled
                        text={isLoading ? 'Guardando...' : 'Guardar contraseña'}
                        isDisabled={isLoading}
                        handleClick={onSubmit}
                        customStyle={{ width: { xs: '100%', md: 'auto' } }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ChangePasswordPage;
